import { create } from 'zustand'
import { USER_ROLES } from '@/config/permissions'
import { useAuthStore } from '@/store/useAuthStore'

interface SessionUiState {
  isEsn: boolean | undefined
  isLegacyGrandfathered: boolean | undefined
  disabledSidebarItems: string[]
  menuReady: boolean
  showFreelancerBillingTab: boolean
  freelancerUiReady: boolean
  setTeamUi: (flags: {
    is_esn: boolean
    is_legacy_grandfathered: boolean
    disabled_sidebar_items?: string[]
  }) => void
  setFreelancerUi: (flags: { show_billing_tab: boolean }) => void
  clear: () => void
}

export const useSessionUiStore = create<SessionUiState>((set) => ({
  isEsn: undefined,
  isLegacyGrandfathered: undefined,
  disabledSidebarItems: [],
  menuReady: false,
  showFreelancerBillingTab: false,
  freelancerUiReady: false,
  setTeamUi: (flags) =>
    set({
      isEsn: flags.is_esn,
      isLegacyGrandfathered: flags.is_legacy_grandfathered,
      disabledSidebarItems: flags.disabled_sidebar_items ?? [],
      menuReady: true,
    }),
  setFreelancerUi: (flags) =>
    set({
      showFreelancerBillingTab: flags.show_billing_tab,
      freelancerUiReady: true,
    }),
  clear: () =>
    set({
      isEsn: undefined,
      isLegacyGrandfathered: undefined,
      disabledSidebarItems: [],
      menuReady: false,
      showFreelancerBillingTab: false,
      freelancerUiReady: false,
    }),
}))

/** ESN menu flag — dérivé une fois depuis GET /auth/session. */
export function useIsEsnFromSession(): boolean | undefined {
  const isEsn = useSessionUiStore((state) => state.isEsn)
  const menuReady = useSessionUiStore((state) => state.menuReady)
  const globalRole = useAuthStore((state) => state.globalRole)
  const isTeamRole =
    globalRole === USER_ROLES.TEAM_ADMIN || globalRole === USER_ROLES.TEAM_MEMBER

  if (!isTeamRole) return false
  if (!menuReady) return undefined
  return isEsn === true
}

/** Onglet Facturation réglages — admin équipe non legacy. */
export function useShowBillingTab(): boolean | undefined {
  const isLegacy = useSessionUiStore((state) => state.isLegacyGrandfathered)
  const menuReady = useSessionUiStore((state) => state.menuReady)
  const globalRole = useAuthStore((state) => state.globalRole)
  const isAdmin = globalRole === USER_ROLES.TEAM_ADMIN

  if (!isAdmin) return false
  if (!menuReady) return undefined
  return !isLegacy
}

/** Onglet Facturation réglages freelance — dérivé de GET /auth/session. */
export function useShowFreelancerBillingTab(): boolean {
  const globalRole = useAuthStore((state) => state.globalRole)
  const showBillingTab = useSessionUiStore((state) => state.showFreelancerBillingTab)
  const freelancerUiReady = useSessionUiStore((state) => state.freelancerUiReady)

  if (globalRole !== USER_ROLES.FREELANCER) return false
  if (!freelancerUiReady) return false
  return showBillingTab
}
